/**
 * Overlay layer — renders colored bounding-box hints on the PDF preview pane.
 *
 * Provides a visual cue for document structure: each tagged element on the
 * visible page gets a semi-transparent colored fill + border + label. The
 * fill/border color itself is looked up per-role from `role-palette.ts` —
 * the SAME per-type palette the right-pane structure cards (`cards.ts`
 * `ROLE_TREE`) use, so a box on the left and its card on the right always
 * match. `data-category` / `.cat-*` (below) remain the coarser 8-bucket
 * grouping (`tagCategoryFor` / `OVERLAY_PALETTE`) — still the palette
 * exported for the server manifest / offline trace-report consumers — but
 * they no longer drive the rendered pixel color.
 *
 * Coordinate system note: bbox entries from the C# editor-wasm
 * (`Interop/BoundingBoxes.cs`) are `[x, y, w, h]` in PDF user-space (origin
 * bottom-left, y grows up, units = PDF points). PDF.js viewport is
 * top-left-origin pixel-space. We project through the page's `PageProjection`
 * (`page-projection.js:projectBbox`) — the pdf.js `viewport.transform` carried
 * on the page descriptor — so overlays register with the rasterized frame
 * (CropBox offset + `/Rotate` applied) on every page.
 *
 * Pure module: no global DOM lookups, no implicit state. All inputs are
 * arguments; output is mutation of the supplied `overlay` element. Makes
 * the module unit-testable under jsdom (see test/overlays.test.mjs).
 *
 * @module overlays
 */
import { log } from "./log.js";
import { projectBbox } from "./page-projection.js";
import { ROLE_UNDEFINED } from "./role-constants.js";
import { boxColorForRole } from "../shared/role-palette.js";
/**
 * Eight resize-handle direction tokens. Mirrors `RESIZE_HANDLES` in
 * resize-move.js — kept duplicated here so overlays.js has no dependency
 * on the resize-move controller (overlays.js renders DOM; resize-move.js
 * wires the gesture; both modules read the same dir tokens).
 */
const RESIZE_HANDLE_DIRS = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
/**
 * Minimum visible size (PDF user-space points) a degenerate / position-less
 * bbox is EXPANDED to so the element whose card lists on this page stays
 * locatable + clickable. Bug 5 (USER DIRECTIVE): a null / zero-area box is
 * NEVER dropped — an invisible overlay is worse than a small misplaced one.
 */
const MIN_LOCATABLE_DIM_PT = 24;
// ─── Tag-category palette ─────────────────────────────────────────────────────
/**
 * Default palette + taxonomy. Mirrors `assets/editor-overlay-palette.json`
 * — the canonical manifest both Editor and the offline trace-report
 * (`tools/diagnose/genai-trace-runner.py`) consume per Approach C in
 * `docs/ux/editor-as-canonical-drawing-260505.md`. The bootstrap
 * defaults below let the module render correctly even before
 * `loadOverlayPaletteFromServer()` resolves; once the server-side manifest
 * is fetched, `applyOverlayPaletteManifest()` overlays its values.
 *
 * Hues chosen to be WCAG AA-friendly and distinguishable for the 4-5% of
 * users with red-green deficiency: each pair (Heading/Figure,
 * Paragraph/Caption, etc.) differs in lightness as well as hue.
 *
 * Drift between this default + the manifest is caught by
 * `test/overlays.test.mjs` (which loads the manifest from disk and
 * cross-checks).
 */
const _DEFAULT_OVERLAY_PALETTE = {
    heading: { fill: "rgba(180,  60,  60, 0.12)", border: "rgb(180,  60,  60)", label: "rgb(140,  40,  40)" },
    paragraph: { fill: "rgba( 30, 110, 175, 0.10)", border: "rgb( 30, 110, 175)", label: "rgb( 25,  85, 140)" },
    figure: { fill: "rgba( 40, 140,  90, 0.14)", border: "rgb( 40, 140,  90)", label: "rgb( 30, 105,  70)" },
    table: { fill: "rgba(180, 110,  20, 0.13)", border: "rgb(180, 110,  20)", label: "rgb(140,  85,  15)" },
    list: { fill: "rgba(120,  60, 165, 0.13)", border: "rgb(120,  60, 165)", label: "rgb( 90,  45, 130)" },
    caption: { fill: "rgba( 70, 140, 165, 0.13)", border: "rgb( 70, 140, 165)", label: "rgb( 50, 105, 130)" },
    annotation: { fill: "rgba(165, 130,  20, 0.13)", border: "rgb(165, 130,  20)", label: "rgb(125,  95,  15)" },
    other: { fill: "rgba( 90,  90,  90, 0.10)", border: "rgb(120, 120, 120)", label: "rgb( 80,  80,  80)" },
};
const _DEFAULT_ROLE_TO_CATEGORY = {
    "H": "heading", "H1": "heading", "H2": "heading", "H3": "heading",
    "H4": "heading", "H5": "heading", "H6": "heading",
    "P": "paragraph", "BlockQuote": "paragraph", "Quote": "paragraph",
    "Span": "paragraph",
    "Figure": "figure", "Form": "figure", "Formula": "figure",
    "Table": "table", "TR": "table", "TH": "table", "TD": "table",
    "THead": "table", "TBody": "table", "TFoot": "table",
    "L": "list", "LI": "list", "Lbl": "list", "LBody": "list",
    "TOC": "list", "TOCI": "list",
    "Caption": "caption",
    "Note": "annotation", "Reference": "annotation",
    "Annot": "annotation", "Link": "annotation",
};
// Module-internal mutable state. Bootstrapped from defaults; replaced by
// `applyOverlayPaletteManifest()` once the canonical manifest loads.
// Typed as Record<string, ...> so dynamic role/category keys (from the
// server-supplied manifest) index without TS7053 implicit-any errors.
const _categories = { ..._DEFAULT_OVERLAY_PALETTE };
const _roleToCategory = { ..._DEFAULT_ROLE_TO_CATEGORY };
/**
 * Frozen view of the active category palette. Render code should treat this
 * as read-only; mutation goes through `applyOverlayPaletteManifest()`.
 *
 * Reference identity is preserved across manifest reloads — the same object
 * is mutated in-place — so importers that capture it at module-load (tests,
 * for example) see post-load updates without re-importing.
 */
export const OVERLAY_PALETTE = _categories;
/**
 * Map a PDF structure role token to a category key in `OVERLAY_PALETTE`.
 *
 * Categories follow the spec-tag taxonomy in
 * `docs/design/spa-a11y-editor-260430.md` §5: groupings reflect what a
 * sighted editor needs to distinguish at a glance — headings stand out
 * red, body text blue, figures green, tabular data orange, lists purple.
 *
 * The mapping table is sourced from
 * `assets/editor-overlay-palette.json:roleToCategory`. Roles not in that
 * table fall through to "other".
 */
export function tagCategoryFor(role) {
    if (!role)
        return "other";
    return _roleToCategory[String(role)] ?? "other";
}
/**
 * Apply a parsed `editor-overlay-palette.json` manifest. Replaces the
 * default category palette and role→category lookup with the manifest's
 * values in-place. Subsequent `renderOverlays` calls see the new colours
 * immediately.
 */
export function applyOverlayPaletteManifest(manifest) {
    if (!manifest || typeof manifest !== "object")
        return;
    if (manifest.categories && typeof manifest.categories === "object") {
        // Replace contents in-place to preserve `OVERLAY_PALETTE` identity
        // (importers capture it once; we don't want them stuck on stale data).
        for (const k of Object.keys(_categories))
            delete _categories[k];
        Object.assign(_categories, manifest.categories);
    }
    if (manifest.roleToCategory && typeof manifest.roleToCategory === "object") {
        for (const k of Object.keys(_roleToCategory))
            delete _roleToCategory[k];
        Object.assign(_roleToCategory, manifest.roleToCategory);
    }
}
/**
 * Fetch the canonical overlay-palette manifest from the server and apply
 * it. Idempotent — repeated calls are cheap (browser caches the response).
 * Resolves with `true` on success, `false` on any network / parse error
 * (the defaults remain in place).
 *
 * The endpoint serves `assets/editor-overlay-palette.json` directly; see
 * `web/server.py:get_editor_overlay_palette`.
 */
export async function loadOverlayPaletteFromServer() {
    try {
        const resp = await fetch("/api/editor/overlay-palette", {
            credentials: "same-origin",
        });
        if (!resp.ok)
            return false;
        const manifest = await resp.json();
        applyOverlayPaletteManifest(manifest);
        return true;
    }
    catch (err) {
        // Defensive: the editor renders fine on the bootstrap defaults;
        // a manifest fetch failure is non-fatal. Surface to console so a
        // dev catches the drift, but don't throw.
        log.warn("[overlays] palette manifest fetch failed; defaults used:", err);
        return false;
    }
}
/**
 * Compact per-tag display label for the badge. Long roles (Document,
 * BlockQuote) abbreviate; short roles render verbatim.
 */
export function roleAbbrev(role) {
    if (!role)
        return "?";
    const r = String(role);
    if (r.length <= 3)
        return r;
    if (r === "Document")
        return "Doc";
    if (r === "Figure")
        return "Fig";
    if (r === "Caption")
        return "Cap";
    if (r === "Artifact")
        return "Art";
    if (r === "BlockQuote")
        return "Quo";
    if (r === "Reference")
        return "Ref";
    return r.slice(0, 3);
}
export function renderOverlays(opts) {
    const { container, boxes, roleByStructId, projection, selectedStructId = null, selectedStructIds = null, onSelect = null, onBadgeClick = null, onReorderClick = null, visible = true, readingOrderByStructId = null, structIdsWithLongDescNext = null, cardByStructId = null, lockedSnapByStructId = null, suppressedStructIds = null, } = opts;
    const multiSize = selectedStructIds && typeof selectedStructIds.size === "number"
        ? selectedStructIds.size : 0;
    container.innerHTML = "";
    if (!visible)
        return 0;
    if (!Array.isArray(boxes) || boxes.length === 0)
        return 0;
    // Degenerate / not-yet-painted frame: a zero-determinant transform (scale 0)
    // can't place overlays. Mirrors the old `scale <= 0` bail.
    if (!projection)
        return 0;
    const [_ma, _mb, _mc, _md] = projection.userSpaceToDevice;
    if (!(_ma * _md - _mb * _mc))
        return 0;
    // Sort by bbox area descending so larger (containing) boxes are appended
    // first and therefore sit below smaller (inner) boxes in painting order.
    // Smaller boxes are appended last → visually on top → clickable even when
    // a large ancestor bbox covers the same screen region. Stable sort
    // preserves struct-tree order for equal-area boxes.
    const sortedBoxes = [...boxes].sort((a, b) => {
        const areaA = Array.isArray(a?.bbox) ? (a.bbox[2] * a.bbox[3]) : 0;
        const areaB = Array.isArray(b?.bbox) ? (b.bbox[2] * b.bbox[3]) : 0;
        return areaB - areaA;
    });
    let rendered = 0;
    for (const entry of sortedBoxes) {
        if (!entry)
            continue;
        const rawBbox = Array.isArray(entry.bbox) ? entry.bbox : null;
        // Skip only a truly anonymous entry (no bbox AND no structId): there is
        // nothing to locate. Any entry carrying a structId is an element whose
        // card lists on this page, so it MUST stay locatable (bug 5 directive).
        if (!rawBbox && !entry.structId)
            continue;
        let [bx, by, bw, bh] = rawBbox ?? [NaN, NaN, NaN, NaN];
        // Bug 5 (INV-OVL-1, USER DIRECTIVE): a null / zero-area bbox is NOT
        // silently dropped (the old `bw<=0 || bh<=0 → continue` was a fail-quiet
        // omission — rule #8). A wrapper struct elem (Figure via /OBJR, a
        // container whose MCID walk found nothing) emits a degenerate rect;
        // EXPAND it to a visible minimum so the element stays locatable +
        // clickable rather than invisible. Anchor at the element's resolved
        // position when it has one, else at page-origin (truly position-less).
        const isDegenerate = !rawBbox
            || !Number.isFinite(bw) || !Number.isFinite(bh) || bw <= 0 || bh <= 0;
        if (isDegenerate) {
            bx = Number.isFinite(bx) ? bx : 0;
            by = Number.isFinite(by) ? by : 0;
            bw = (Number.isFinite(bw) && bw > 0) ? bw : MIN_LOCATABLE_DIM_PT;
            bh = (Number.isFinite(bh) && bh > 0) ? bh : MIN_LOCATABLE_DIM_PT;
            log.warn(`[overlays] degenerate bbox for struct ${entry.structId} on page ${entry.page} expanded to a ${MIN_LOCATABLE_DIM_PT}pt locate-affordance (bug 5: expand, never drop)`);
        }
        // Suppress a container's internal-layout children on the LEFT pane: a
        // List's item boxes are hidden here (the List's own wrapping box still
        // draws) and stay editable via their right-pane card. See
        // RenderOverlaysOpts.suppressedStructIds (editor-structure-overlay-ux-260904).
        if (suppressedStructIds) {
            const isSuppressed = (typeof suppressedStructIds === "function")
                ? suppressedStructIds(entry.structId)
                : !!(suppressedStructIds.has?.(entry.structId));
            if (isSuppressed)
                continue;
        }
        const rect = projectBbox([bx, by, bw, bh], projection);
        const role = roleByStructId.get(entry.structId) ?? "";
        // `category` / `data-category` / `cat-*` stay on the coarser 8-bucket
        // grouping (kept for the server-manifest / trace-report consumers of
        // OVERLAY_PALETTE — see module docstring); the actual rendered color
        // comes from the per-role shared palette so it matches the right pane.
        const category = tagCategoryFor(role);
        const palette = boxColorForRole(role);
        const div = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        div.className = `editor-overlay-bbox cat-${category}`;
        // Bug 5: mark an expanded-from-degenerate box so CSS renders it as a
        // "located here, geometry unknown" affordance (dashed) and tests can
        // assert the element was surfaced rather than silently dropped.
        if (isDegenerate)
            div.classList.add("is-locate-affordance");
        if (role === ROLE_UNDEFINED)
            div.classList.add("is-undefined");
        const isLockedSnap = lockedSnapByStructId
            ? ((typeof lockedSnapByStructId === "function")
                ? !!lockedSnapByStructId(entry.structId)
                : !!(lockedSnapByStructId.has?.(entry.structId)))
            : false;
        if (isLockedSnap)
            div.classList.add("is-locked-snap");
        div.dataset.structId = entry.structId;
        div.dataset.category = category;
        div.dataset.role = role;
        // Stamp the document-wide reading-order index on the overlay element
        // itself (in addition to the inner order-chip's data-order). Doing
        // both lets specs query a single attribute on the overlay div without
        // a child traversal — `[data-reading-order='1']` matches the bbox
        // directly. Defensive: only set when the lookup yields a real number.
        if (readingOrderByStructId) {
            const order = (typeof readingOrderByStructId === "function")
                ? readingOrderByStructId(entry.structId)
                : (readingOrderByStructId.get?.(entry.structId));
            if (typeof order === "number" && order > 0) {
                div.dataset.readingOrder = String(order);
            }
        }
        div.style.left = `${rect.x}px`;
        div.style.top = `${rect.y}px`;
        div.style.width = `${rect.w}px`;
        div.style.height = `${rect.h}px`;
        div.style.backgroundColor = palette.fill;
        div.style.borderColor = palette.border;
        div.tabIndex = 0;
        // Tooltip = what a screen reader would say for this element. Built
        // from the matching card via screen-reader.js:transcriptLineFor +
        // speakableTextForLine — single source of truth for the audible
        // surface, so the hover preview matches the actual screen-reader
        // output. Fallback to role name when no card maps (Sect/Div/Part
        // structural containers — screen readers don't announce these).
        let titleText = role || "Element";
        if (cardByStructId) {
            const card = (typeof cardByStructId === "function")
                ? cardByStructId(entry.structId)
                : (cardByStructId.get?.(entry.structId));
            if (card) {
                try {
                    const line = transcriptLineFor(card);
                    const spoken = speakableTextForLine(line, "full");
                    if (spoken)
                        titleText = spoken;
                }
                catch {
                    // Defensive: fall back to role on any composer error.
                }
            }
        }
        div.setAttribute("title", titleText);
        div.setAttribute("role", "button");
        div.setAttribute("aria-label", role
            ? `Structure element ${role} on page ${entry.page}`
            : `Structure element on page ${entry.page}`);
        // Cluster the role chip + reading-order chip in a single
        // upper-left controls bar. The CSS positions the bar ABOVE the
        // bbox via translateY(-100%) so chips don't cover the first line
        // of glyphs. When the bbox sits within ~22px of the page top
        // there's no room above; add `.is-affordance-clipped` so CSS
        // falls back to inside-the-box placement.
        //
        // CHIP_STRIP_HEIGHT_PX = 22 covers role-badge (~16px) + 2px
        // breathing margin + a little safety. Higher than the actual chip
        // strip — better to err inside-the-box than chop the chip.
        const CHIP_STRIP_HEIGHT_PX = 22;
        const controlsRow = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        controlsRow.className = "editor-overlay-controlbar";
        if (rect.y < CHIP_STRIP_HEIGHT_PX) {
            controlsRow.classList.add("is-affordance-clipped");
        }
        // Don't render labels on tiny boxes — the chips would obscure the
        // entire region. A 14×14 minimum keeps the visual cue useful.
        const showControls = rect.w >= 24 && rect.h >= 14;
        if (showControls) {
            // The role badge is a button (with role) when the host wants
            // to open the type-picker on click. Otherwise a passive span.
            const badge = document.createElement(onBadgeClick ? "button" : "span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            badge.className = "editor-overlay-rolebadge";
            badge.textContent = roleAbbrev(role);
            badge.style.backgroundColor = palette.border;
            if (onBadgeClick) {
                badge.type = "button";
                badge.title = "Click to change element type";
                badge.setAttribute("aria-label", `Change type — currently ${role || "unknown"}`);
                badge.addEventListener("click", (ev) => {
                    ev.stopPropagation();
                    ev.preventDefault();
                    onBadgeClick(entry.structId, ev);
                });
                // Pointerdown often initiates an ancestor's drag/resize/
                // create gesture before the click reaches the badge. Stop
                // it here so a click on the chip never accidentally starts
                // a drag-to-create on the underlying overlay surface.
                badge.addEventListener("pointerdown", (ev) => { ev.stopPropagation(); });
            }
            controlsRow.appendChild(badge);
            // Reading-order chip: numeric label + (when interactive)
            // up/down arrows. Both arrows are real <button>s so the
            // gesture is real-click-able under Playwright real-event
            // tests. When the next block in reading order is an AdaTool-
            // stamped long-description paragraph (no MCID → no overlay of
            // its own), the chip renders "X, X+1" so the figure ↔ long-
            // desc association is visible at a glance, and reorder arrows
            // are suppressed to keep the pair from being separated. Per
            // docs/design/long-description-policy-audit-260513.md §C.1.1.
            if (readingOrderByStructId) {
                const order = (typeof readingOrderByStructId === "function")
                    ? readingOrderByStructId(entry.structId)
                    : (readingOrderByStructId.get?.(entry.structId));
                if (typeof order === "number" && order > 0) {
                    const hasLongDescNext = structIdsWithLongDescNext
                        ? ((typeof structIdsWithLongDescNext === "function")
                            ? !!structIdsWithLongDescNext(entry.structId)
                            : !!(structIdsWithLongDescNext.has?.(entry.structId)))
                        : false;
                    const labelText = hasLongDescNext ? `${order}, ${order + 1}` : String(order);
                    const orderChip = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
                    orderChip.className = "editor-overlay-order-chip";
                    if (hasLongDescNext)
                        orderChip.classList.add("is-range-with-longdesc");
                    orderChip.dataset.order = String(order);
                    if (hasLongDescNext)
                        orderChip.dataset.orderRangeEnd = String(order + 1);
                    orderChip.style.color = palette.border;
                    orderChip.style.borderColor = palette.border;
                    orderChip.setAttribute("aria-label", hasLongDescNext
                        ? `Reading order ${order} and ${order + 1} — the next block is an AdaTool long-description paragraph`
                        : `Reading order ${order}`);
                    // Suppress reorder arrows on range-labelled chips —
                    // moving "X" alone would orphan "X+1" from its
                    // owning figure. Same suppression-on-coupled-pair
                    // pattern used for locked-snap overlays.
                    const showArrows = typeof onReorderClick === "function" && !hasLongDescNext;
                    if (showArrows) {
                        const upBtn = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
                        upBtn.type = "button";
                        upBtn.className = "editor-overlay-order-arrow up";
                        upBtn.textContent = "▲";
                        upBtn.title = "Move earlier in reading order";
                        upBtn.setAttribute("aria-label", "▲ Move earlier in reading order");
                        upBtn.addEventListener("click", (ev) => {
                            ev.stopPropagation();
                            ev.preventDefault();
                            onReorderClick(entry.structId, "up", ev);
                        });
                        upBtn.addEventListener("pointerdown", (ev) => { ev.stopPropagation(); });
                        orderChip.appendChild(upBtn);
                    }
                    const numEl = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
                    numEl.className = "editor-overlay-order-num";
                    numEl.textContent = labelText;
                    orderChip.appendChild(numEl);
                    if (showArrows) {
                        const dnBtn = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
                        dnBtn.type = "button";
                        dnBtn.className = "editor-overlay-order-arrow down";
                        dnBtn.textContent = "▼";
                        dnBtn.title = "Move later in reading order";
                        dnBtn.setAttribute("aria-label", "▼ Move later in reading order");
                        dnBtn.addEventListener("click", (ev) => {
                            ev.stopPropagation();
                            ev.preventDefault();
                            onReorderClick(entry.structId, "down", ev);
                        });
                        dnBtn.addEventListener("pointerdown", (ev) => { ev.stopPropagation(); });
                        orderChip.appendChild(dnBtn);
                    }
                    controlsRow.appendChild(orderChip);
                }
            }
            div.appendChild(controlsRow);
        }
        // Alt-text hover tooltip on description-bearing overlays.
        // Visible on mouseenter / focus; cleared on mouseleave / blur.
        const isDescriptionRole = (role === "Figure" || role === "Audio" || role === "Video"
            || role === "Table" || role === "Formula");
        if (isDescriptionRole && cardByStructId) {
            const card = (typeof cardByStructId === "function")
                ? cardByStructId(entry.structId)
                : (cardByStructId.get?.(entry.structId));
            const altText = (card && typeof card.alt === "string") ? card.alt.trim() : "";
            const tip = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            tip.className = "editor-overlay-alt-tooltip";
            tip.setAttribute("role", "tooltip");
            const lbl = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            lbl.className = "editor-overlay-alt-tooltip-label";
            lbl.textContent = role === "Figure" ? "Alt text" : "Description";
            tip.appendChild(lbl);
            const tx = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            tx.className = altText ? "editor-overlay-alt-tooltip-text" : "editor-overlay-alt-tooltip-empty";
            tx.textContent = altText || "(no alt text)";
            tip.appendChild(tx);
            div.appendChild(tip);
            // Flip the tooltip above the overlay when the overlay's bottom
            // is within ~120px of the viewport bottom — otherwise the
            // tooltip clips off-screen or overlays the next page. Mockup
            // ref: card-gui §showAltTooltip "Decide if tooltip fits below
            // or needs to go above" (line ~2229).
            const show = () => {
                tip.classList.add("is-visible");
                if (typeof div.getBoundingClientRect === "function"
                    && typeof window !== "undefined") {
                    try {
                        const r = div.getBoundingClientRect();
                        const spaceBelow = (window.innerHeight || 0) - r.bottom; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
                        if (spaceBelow < 120)
                            tip.classList.add("editor-overlay-alt-tooltip-above");
                        else
                            tip.classList.remove("editor-overlay-alt-tooltip-above");
                    }
                    catch (_) {
                        // Defensive: in jsdom-stub tests innerHeight may be undefined;
                        // tooltip stays in its default below-position.
                    }
                }
            };
            const hide = () => tip.classList.remove("is-visible");
            div.addEventListener("mouseenter", show);
            div.addEventListener("mouseleave", hide);
            div.addEventListener("focus", show);
            div.addEventListener("blur", hide);
        }
        const inMultiSet = selectedStructIds?.has(entry.structId);
        if (entry.structId === selectedStructId || inMultiSet) {
            div.classList.add("is-selected");
            if (multiSize > 1 && inMultiSet) {
                div.classList.add("is-multi-selected");
            }
        }
        // Resize handles (8 per box: 4 corners + 4 edges). The
        // ResizeMoveController in resize-move.js dispatches on
        // `.editor-resize-handle` pointerdown by reading data-dir.
        // Handles are CSS-positioned; pointer-events: auto only on the
        // handles themselves (the badges remain pointer-events: none).
        //
        // Suppress handles entirely on locked-snap overlays: the geometry
        // is derived from existing structure (snap-on-all-sides during
        // insert), so a draggable affordance would let the user fight
        // the snap. Bug 2.
        if (!isLockedSnap) {
            for (const dir of RESIZE_HANDLE_DIRS) {
                const h = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
                h.className = `editor-resize-handle handle-${dir}`;
                h.dataset.dir = dir;
                h.setAttribute("aria-hidden", "true");
                div.appendChild(h);
            }
        }
        if (typeof onSelect === "function") {
            div.addEventListener("click", (ev) => {
                ev.stopPropagation();
                onSelect(entry.structId, ev);
            });
            div.addEventListener("keydown", (ev) => {
                const kev = ev;
                if (kev.key === "Enter" || kev.key === " ") {
                    ev.preventDefault();
                    onSelect(entry.structId, kev);
                }
            });
        }
        container.appendChild(div);
        rendered++;
    }
    return rendered;
}
/**
 * Build the structId → role lookup once per struct-tree load.
 * Convenience helper so callers don't have to inline the reduce.
 */
export function buildRoleMap(nodes) {
    const map = new Map();
    if (!Array.isArray(nodes))
        return map;
    for (const n of nodes) {
        if (n?.structId)
            map.set(n.structId, n.role || "");
    }
    return map;
}
/**
 * Update the is-selected class on already-rendered overlay boxes without
 * a full re-render. Cheaper than `renderOverlays` when only selection
 * changes (e.g., clicking a sidebar entry).
 */
export function updateOverlaySelection(container, selected) {
    if (!container)
        return;
    const isSet = selected instanceof Set;
    const single = (!isSet && typeof selected === "string") ? selected : null;
    const size = isSet ? selected.size : (single ? 1 : 0);
    for (const box of container.querySelectorAll(".editor-overlay-bbox")) {
        const id = box.dataset.structId;
        const sel = isSet ? selected.has(id ?? "") : (id === single);
        box.classList.toggle("is-selected", sel);
        box.classList.toggle("is-multi-selected", sel && size > 1);
    }
}
